import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Package, MapPin, Clock, AlertTriangle, User } from 'lucide-react';
import { MOCK_ASSETS, ASSET_TYPES, AGING_THRESHOLDS, CUSTOMERS } from '../services/mockData';
import '../styles/Assets.css';

const Assets = () => {
  const navigate = useNavigate();
  const [assets] = useState(MOCK_ASSETS);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [warehouseFilter, setWarehouseFilter] = useState('ALL');

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'ALL' || asset.type === typeFilter;
    const matchesStatus = statusFilter === 'ALL' || asset.status === statusFilter;
    const matchesWarehouse = warehouseFilter === 'ALL' || 
                            asset.currentWarehouse === warehouseFilter ||
                            (warehouseFilter === 'IN_TRANSIT' && !asset.currentWarehouse && !asset.currentCustomer);
    
    return matchesSearch && matchesType && matchesStatus && matchesWarehouse;
  });

  // Get unique warehouses from assets
  const warehouses = [...new Set(assets.map(a => a.currentWarehouse).filter(Boolean))].sort();

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'AT_WAREHOUSE': 'status-warehouse',
      'IN_TRANSIT': 'status-transit',
      'AT_CUSTOMER': 'status-customer',
      'LOADING': 'status-loading',
      'UNLOADING': 'status-unloading',
      'MAINTENANCE': 'status-maintenance'
    };
    return statusMap[status] || 'status-default';
  };

  const handleAssetClick = (assetId) => {
    navigate(`/assets/${assetId}`);
  };

  return (
    <div className="assets-page">
      <div className="page-header">
        <div>
          <h1>Asset Management</h1>
          <p className="page-subtitle">
            Showing {filteredAssets.length} of {assets.length} assets
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by asset ID or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="ALL">All Types</option>
            {Object.keys(ASSET_TYPES).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="AT_WAREHOUSE">At Warehouse</option>
            <option value="IN_TRANSIT">In Transit</option>
            <option value="LOADING">Loading</option>
            <option value="UNLOADING">Unloading</option>
            <option value="MAINTENANCE">Maintenance</option>
          </select>

          <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)}>
            <option value="ALL">All Locations</option>
            <option value="IN_TRANSIT">In Transit</option>
            {warehouses.map(wh => (
              <option key={wh} value={wh}>{wh}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Assets Transfer Tracking Table */}
      <div className="assets-table-container transfer-tracking">
        <div className="table-wrapper">
          <table className="transfer-table">
            <thead>
              <tr className="header-row-1">
                <th rowSpan="2" className="col-asset-type">Asset Type</th>
                <th rowSpan="2" className="col-asset-id">Asset ID</th>
                <th colSpan="4" className="warehouse-group pune-origin">Origin</th>
                <th colSpan="4" className="warehouse-group bangalore">Tier-1</th>
                <th colSpan="4" className="warehouse-group chennai">OEM</th>
                <th colSpan="4" className="warehouse-group pune-return">Return</th>
              </tr>
              <tr className="header-row-2">
                {/* Origin */}
                <th className="col-wh pune-origin">WH</th>
                <th className="col-transfer-in pune-origin">Transfer IN</th>
                <th className="col-age pune-origin">Age (days)</th>
                <th className="col-transfer-out pune-origin">Transfer OUT</th>

                {/* Tier-1 */}
                <th className="col-wh bangalore">WH</th>
                <th className="col-transfer-in bangalore">Transfer IN</th>
                <th className="col-age bangalore">Age (days)</th>
                <th className="col-transfer-out bangalore">Transfer OUT</th>

                {/* OEM */}
                <th className="col-wh chennai">WH</th>
                <th className="col-transfer-in chennai">Transfer IN</th>
                <th className="col-age chennai">Age (days)</th>
                <th className="col-transfer-out chennai">Transfer OUT</th>

                {/* Return */}
                <th className="col-wh pune-return">WH</th>
                <th className="col-transfer-in pune-return">Transfer IN</th>
                <th className="col-age pune-return">Age (days)</th>
                <th className="col-transfer-out pune-return">Transfer OUT</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan="18" className="no-data">
                    <Package size={48} />
                    <p>No assets found matching your criteria</p>
                  </td>
                </tr>
              ) : (
                filteredAssets.slice(0, 9).map(asset => {
                  const transfers = asset.transferHistory || [];
                  const threshold = AGING_THRESHOLDS[asset.type];

                  // Get transfer data for each warehouse
                  const puneOrigin = transfers[0] || {};
                  const bangalore = transfers[1] || {};
                  const chennai = transfers[2] || {};
                  const puneReturn = transfers[3] || {};

                  const formatDate = (dateStr) => {
                    if (!dateStr) return '-';
                    const date = new Date(dateStr);
                    return date.toLocaleString('en-IN', {
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    });
                  };

                  const getAgeClass = (age, threshold) => {
                    if (!age) return '';
                    return age > threshold ? 'age-exceeded' : '';
                  };

                  return (
                    <tr
                      key={asset.id}
                      onClick={() => handleAssetClick(asset.id)}
                      className="asset-row"
                    >
                      <td className="col-asset-type">
                        <span className={`type-badge type-${asset.type.toLowerCase()}`}>{asset.type}</span>
                      </td>
                      <td className="col-asset-id">
                        <strong>{asset.id}</strong>
                      </td>

                      {/* Pune Origin */}
                      <td className="col-wh pune-origin">{puneOrigin.warehouse || 'Pune'}</td>
                      <td className="col-transfer-in pune-origin">{formatDate(puneOrigin.transferIn)}</td>
                      <td className={`col-age pune-origin ${getAgeClass(puneOrigin.ageDays, threshold)}`}>
                        {puneOrigin.ageDays || '-'}
                      </td>
                      <td className="col-transfer-out pune-origin">{formatDate(puneOrigin.transferOut)}</td>

                      {/* Bangalore */}
                      <td className="col-wh bangalore">{bangalore.warehouse || '-'}</td>
                      <td className="col-transfer-in bangalore">{formatDate(bangalore.transferIn)}</td>
                      <td className={`col-age bangalore ${getAgeClass(bangalore.ageDays, threshold)}`}>
                        {bangalore.ageDays || '-'}
                      </td>
                      <td className="col-transfer-out bangalore">{formatDate(bangalore.transferOut)}</td>

                      {/* Chennai */}
                      <td className="col-wh chennai">{chennai.warehouse || '-'}</td>
                      <td className="col-transfer-in chennai">{formatDate(chennai.transferIn)}</td>
                      <td className={`col-age chennai ${getAgeClass(chennai.ageDays, threshold)}`}>
                        {chennai.ageDays || '-'}
                      </td>
                      <td className="col-transfer-out chennai">{formatDate(chennai.transferOut)}</td>

                      {/* Pune Return */}
                      <td className="col-wh pune-return">{puneReturn.warehouse || '-'}</td>
                      <td className="col-transfer-in pune-return">{formatDate(puneReturn.transferIn)}</td>
                      <td className={`col-age pune-return ${getAgeClass(puneReturn.ageDays, threshold)}`}>
                        {puneReturn.ageDays || '-'}
                      </td>
                      <td className="col-transfer-out pune-return">{formatDate(puneReturn.transferOut)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="assets-summary">
        <div className="summary-card">
          <h3>Assets by Type</h3>
          <div className="summary-stats">
            {Object.keys(ASSET_TYPES).map(type => {
              const count = assets.filter(a => a.type === type).length;
              return (
                <div key={type} className="summary-stat-item">
                  <span className="stat-label">{type}:</span>
                  <span className="stat-value">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="summary-card">
          <h3>Assets by Status</h3>
          <div className="summary-stats">
            <div className="summary-stat-item">
              <span className="stat-label">At Warehouse:</span>
              <span className="stat-value">{assets.filter(a => a.status === 'AT_WAREHOUSE').length}</span>
            </div>
            <div className="summary-stat-item">
              <span className="stat-label">In Transit:</span>
              <span className="stat-value">{assets.filter(a => a.status === 'IN_TRANSIT').length}</span>
            </div>
          </div>
        </div>

        <div className="summary-card alert-card">
          <h3>Aging Violations</h3>
          <div className="summary-stats">
            <div className="summary-stat-item">
              <AlertTriangle size={20} color="#ef4444" />
              <span className="stat-value large">{assets.filter(a => a.exceedsThreshold).length}</span>
              <span className="stat-label">assets exceeding threshold</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assets;

